import { DOCUMENT, Injectable, NgZone, OnDestroy, inject } from "@angular/core";
import { LanguageService } from "./language.service";

/**
 * Sent by the page embedding this widget in a frame, to say which language it is being read in.
 *
 * A language carries no authority: it is a preference, not a credential, and the worst a stranger
 * posting it can do is show the widget in another language. It is therefore accepted from the
 * embedding page without an origin list, which is what keeps the embed working with no
 * configuration, while credentials keep their own strictly listed channel.
 */
export const LANGUAGE_MESSAGE = "chatbot:language";

/** Sent by the widget on startup, so a page that embeds it does not have to guess when to speak. */
export const LANGUAGE_REQUEST = "chatbot:language-request";

interface LanguageMessage {
    type: typeof LANGUAGE_MESSAGE;
    lang?: string | null;
}

/**
 * Follows the language of the page this widget is framed inside.
 *
 * An embedding host shares its translation service, so the widget simply reads the language from
 * it. Across a frame boundary there is nothing to share, so the page says it instead. A language the
 * widget does not speak falls back to English, because the page asking has already moved on to it.
 */
@Injectable({ providedIn: "root" })
export class IframeLanguageService implements OnDestroy {
    private readonly language = inject(LanguageService);
    private readonly zone = inject(NgZone);
    private readonly document = inject(DOCUMENT);
    private listener?: (event: MessageEvent) => void;

    /** Starts listening, and asks the embedding page once, in case it is already waiting. */
    connect(): void {
        const view = this.document.defaultView;
        const parent = view?.parent;
        if (!view || !parent || parent === view) {
            return;
        }

        this.listener = (event: MessageEvent) => {
            const data = event.data as LanguageMessage | null;
            if (!data || data.type !== LANGUAGE_MESSAGE || !data.lang) {
                return;
            }
            // postMessage callbacks run outside Angular's zone, and what changes here is what the
            // whole widget reads from.
            this.zone.run(() => this.language.applyRequestedLanguage(data.lang));
        };

        view.addEventListener("message", this.listener);
        parent.postMessage({ type: LANGUAGE_REQUEST }, "*");
    }

    disconnect(): void {
        const view = this.document.defaultView;
        if (this.listener && view) {
            view.removeEventListener("message", this.listener);
            this.listener = undefined;
        }
    }

    ngOnDestroy(): void {
        this.disconnect();
    }
}
